import { useState, useMemo } from "react";
import useCategories from "../../../hooks/features/categories/useCategories";
import { Categorie } from "../../../types/features/Post";
import PageLayout from "../../../layouts/PageLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import { Plus, Search } from "lucide-react";
import CategoriesList from "../../../components/shared/blog/categories/CategoriesList";
import CategorieModal from "../../../components/shared/blog/categories/CategorieModal";
import { useProduct } from "../../../hooks/core/useProduct";

const CategoriesPage = () => {
  const { categories, loading, deleteCategorie } = useCategories();
  const { selectedProduct } = useProduct();

  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Categorie | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [isDeleting, setIsDeleting] = useState(false);

  // ✅ Catégories filtrées par produit (pour les stats)
  const categoriesByProduct = useMemo(() => {
    if (selectedProduct && selectedProduct !== 'all') {
      return categories.filter((cat) => cat.product_name === selectedProduct);
    }
    return categories;
  }, [categories, selectedProduct]);

  // ✅ Filtrer les catégories par recherche (à partir des catégories déjà filtrées par produit)
  const filteredCategories = useMemo(() => {
    let result = categoriesByProduct;

    // Filtrer par recherche
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(
        (cat) =>
          cat.name.toLowerCase().includes(searchLower) ||
          cat.name_en.toLowerCase().includes(searchLower) ||
          (cat.description && cat.description.toLowerCase().includes(searchLower)) ||
          (cat.description_en && cat.description_en.toLowerCase().includes(searchLower))
      );
    }

    return result;
  }, [categoriesByProduct, searchTerm]);

  const handleCreate = () => {
    setSelectedCategory(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const handleEdit = (categorie: Categorie) => {
    setSelectedCategory(categorie);
    setModalMode("edit");
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedCategory) {
      setIsDeleting(true);
      await deleteCategorie(selectedCategory.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setSelectedCategory(null);
    }
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);
  };

  const handleSuccess = () => {
    setModalOpen(false);
    setSelectedCategory(null);
  };

  return (
    <PageLayout title="Gérer les catégories" className="min-h-screen bg-gray-50">
      {/* Header avec bouton ajouter et recherche */}
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Catégories</h1>
              <p className="text-sm text-gray-500 mt-1">Gérez les catégories d'articles de blog</p>
            </div>
            <button
              onClick={handleCreate}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
            >
              <Plus size={18} />
              Nouvelle catégorie
            </button>
          </div>

          {/* Indicateur de filtre */}
          {selectedProduct && selectedProduct !== 'all' && (
            <div className="mt-3 text-sm text-gray-500">
              Filtré par : <span className="font-medium text-gray-700">{selectedProduct}</span>
            </div>
          )}

          {/* Stats des catégories filtrées */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Total</p>
              <p className="text-2xl font-bold text-blue-700">{categoriesByProduct.length}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Avec articles</p>
              <p className="text-2xl font-bold text-green-700">
                {categoriesByProduct.filter((cat) => (cat.post_count ?? 0) > 0).length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-700/70">Sans article</p>
              <p className="text-2xl font-bold text-gray-700">
                {categoriesByProduct.filter((cat) => (cat.post_count ?? 0) === 0).length}
              </p>
            </div>
          </div>

          {/* Barre de recherche */}
          <div className="relative mt-4">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une catégorie..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-gray-500">
              {filteredCategories.length} catégorie(s) affichée(s)
            </div>
            <CategoriesList
              categories={filteredCategories}
              onAction={(categorie) => {
                handleEdit(categorie);
              }}
              onSelectCategories={(selected) => {
                console.log("Selected categories:", selected);
              }}
              selectable={true}
              selectActions={[
                {
                  label: "Supprimer",
                  onClick: (selected) => {
                    selected.forEach((cat) => deleteCategorie(cat.id));
                  },
                  className: "bg-red-600 text-white",
                },
              ]}
            />
          </>
        )}
      </div>

      {/* Modal de création/édition */}
      {modalOpen && (
        <CategorieModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          mode={modalMode}
          categorie={selectedCategory || undefined}
          onSuccess={handleSuccess}
        />
      )}

      {/* Modal de suppression */}
      {deleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          title="Supprimer la catégorie"
          message={
            <div>
              <p>Êtes-vous sûr de vouloir supprimer cette catégorie ?</p>
              {(selectedCategory?.post_count ?? 0) > 0 && (
                <p className="mt-2 text-red-600 font-medium">
                  Attention : {selectedCategory?.post_count} article(s) utilisent cette catégorie. La suppression est
                  impossible tant que des articles y sont rattachés.
                </p>
              )}
            </div>
          }
          confirmText="Supprimer définitivement"
          cancelText="Annuler"
          isLoading={isDeleting}
        />
      )}
    </PageLayout>
  );
};

export default CategoriesPage;